import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Hash,
  User,
  ArrowRight,
  Zap,
  Clock,
  Flame,
  Calendar,
  AlertTriangle,
  Trophy,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

// New 2026 generic-code flow:
//   1. User types a code  → POST /codes/validate
//   2. We branch on status:
//      - available → fetch /challenges/available-for-code, show open + upcoming
//      - reserved  → show the locked-in challenge
//      - consumed  → resume the existing session
//      - completed → leaderboard / claim
//      - no_show / cancelled / refunded → terminal explanation
//   3. On "Jouer maintenant" → POST /play/sessions/start with challenge_id
//   4. On "Inscription anticipée" → confirmation modal → POST /codes/pre-register
export default function PlayCode() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [code, setCode] = useState(params.get("code") || "");
  const [nickname, setNickname] = useState("");
  const [phase, setPhase] = useState("input"); // input | choose | starting
  const [validated, setValidated] = useState(null);
  const [available, setAvailable] = useState({ open: [], upcoming: [] });
  const [loading, setLoading] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null); // upcoming challenge being confirmed

  // Auto-validate if code was pre-filled (deep link from /mes-codes or email)
  useEffect(() => {
    if (params.get("code") && code) {
      handleValidate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleValidate = async () => {
    if (!code.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/codes/validate", {
        code: code.trim().toUpperCase(),
      });
      setValidated(data);
      if (data.status === "available") {
        const { data: list } = await api.get("/challenges/available-for-code");
        setAvailable(list);
        setPhase("choose");
      } else if (data.status === "reserved") {
        setPhase("choose"); // single locked challenge
      } else if (data.status === "consumed" && data.play_session_id) {
        navigate(`/play/session/${data.play_session_id}`);
      } else if (data.status === "completed" && data.play_session_id) {
        navigate(`/play/session/${data.play_session_id}`);
      } else {
        setPhase("choose"); // terminal message handled in render
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const playNow = async (challenge_id) => {
    if (!user?.nickname) {
      navigate("/profile?need=nickname&next=/play");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/play/sessions/start", {
        code: code.trim().toUpperCase(),
        challenge_id,
      });
      navigate(`/play/session/${data.session_id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const preRegister = async () => {
    if (!confirmTarget) return;
    setLoading(true);
    try {
      await api.post("/codes/pre-register", {
        code: code.trim().toUpperCase(),
        challenge_id: confirmTarget.id,
      });
      toast.success(`Code réservé sur « ${confirmTarget.title} »`);
      setConfirmTarget(null);
      navigate("/mes-codes");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // ---------- Auth gate ----------
  // Spec 2026: code usage requires authentication so ownership is enforceable.
  if (user === false) {
    return (
      <Shell>
        <div className="space-y-4 text-center" data-testid="play-login-required">
          <h1 className="font-display text-3xl font-bold">Connexion requise</h1>
          <p className="text-white/55 text-sm">
            Connecte-toi pour valider et utiliser ton code personnel.
          </p>
          <button
            data-testid="play-login-button"
            onClick={() => {
              try {
                if (code)
                  sessionStorage.setItem("otra_play_code_pending", code.trim().toUpperCase());
              } catch (_) {}
              navigate("/login?next=/play");
            }}
            className="w-full px-6 py-4 rounded-xl bg-cyan-400 text-black font-display font-bold uppercase tracking-wider hover:bg-cyan-300 active:scale-95 transition-all glow-primary"
          >
            Se connecter
          </button>
        </div>
      </Shell>
    );
  }

  // ---------- Input phase ----------
  if (phase === "input") {
    return (
      <Shell>
        <div className="space-y-4">
          <span className="text-xs uppercase tracking-[0.3em] text-cyan-300 font-mono-stat">
            J&apos;ai un code
          </span>
          <h1 className="font-display text-3xl font-bold">Saisis ton code</h1>
          <p className="text-white/55 text-sm">
            Ton code est valide tant qu&apos;il n&apos;a pas été consommé. Tu pourras choisir
            entre jouer un défi ouvert maintenant ou en réserver un à venir.
          </p>
          <CodeField code={code} setCode={setCode} />
          <button
            data-testid="play-validate-button"
            onClick={handleValidate}
            disabled={loading || code.length < 4}
            className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-cyan-400 text-black font-display font-bold uppercase tracking-wider hover:bg-cyan-300 active:scale-95 disabled:opacity-50 transition-all glow-primary"
          >
            {loading ? "Vérification..." : "Valider mon code"}
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </Shell>
    );
  }

  // ---------- Terminal / locked states ----------
  if (validated?.status === "no_show" || validated?.status === "expired") {
    return (
      <Shell>
        <Terminal
          icon={<Clock className="w-10 h-10 text-orange-300" />}
          title="Code expiré"
          body="Tu n'es pas venu jouer pendant la fenêtre d'ouverture du défi réservé. Ce code n'est plus utilisable."
        />
      </Shell>
    );
  }
  if (validated?.status === "cancelled" || validated?.status === "refunded") {
    return (
      <Shell>
        <Terminal
          icon={<AlertTriangle className="w-10 h-10 text-pink-300" />}
          title="Code annulé"
          body="Ce code a été annulé. Contacte le support si tu penses qu'il s'agit d'une erreur."
        />
      </Shell>
    );
  }

  // ---------- Choose phase (available OR reserved) ----------
  return (
    <Shell wide>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <span className="text-xs uppercase tracking-[0.3em] text-cyan-300 font-mono-stat">
            Code {code}
          </span>
          <h1 className="font-display text-3xl font-bold mt-1">
            {validated?.status === "reserved"
              ? "Tu as réservé un défi"
              : "Choisis ton défi"}
          </h1>
        </div>
        <button
          data-testid="play-back-button"
          onClick={() => {
            setPhase("input");
            setValidated(null);
          }}
          className="text-xs text-white/50 hover:text-cyan-300 font-mono-stat uppercase tracking-widest"
        >
          ← Changer de code
        </button>
      </div>

      {/* Helper text — explains the flow */}
      <div className="mb-4 text-sm text-white/55">
        {validated?.status === "available"
          ? "Sélectionne un défi ci-dessous. Ton pseudo est celui de ton compte."
          : "Sélectionne ton défi ci-dessous."}
      </div>
      {/* Show the connected nickname (informational) */}
      <div
        data-testid="play-nickname-info"
        className="mb-6 flex items-center gap-2 px-3 py-2 rounded-lg bg-black/30 border border-white/5 text-xs font-mono-stat"
      >
        <User className="w-3.5 h-3.5 text-cyan-300" />
        <span className="text-white/55 uppercase tracking-widest">Pseudo :</span>
        <span className="text-white/90 font-bold">{user?.nickname || "Non défini"}</span>
        <button
          data-testid="play-nickname-edit"
          onClick={() => navigate("/profile?need=nickname&next=/play")}
          className="ml-auto text-[10px] text-cyan-300 hover:text-cyan-200 underline underline-offset-2"
        >
          {user?.nickname ? "Modifier" : "Définir"}
        </button>
      </div>

      {/* Reserved code: show locked challenge */}
      {validated?.status === "reserved" && validated.challenge && (
        <div className="space-y-4">
          <ChallengeRow
            challenge={validated.challenge}
            mode="reserved-locked"
            disabled={validated.challenge.status !== "open"}
            loading={loading}
            onPlay={() => playNow(validated.challenge.id)}
          />
          {validated.challenge.status !== "open" && (
            <p
              data-testid="reserved-not-open-message"
              className="text-xs text-orange-200/70 font-mono-stat uppercase tracking-widest text-center"
            >
              Ce défi ouvre le{" "}
              {new Date(validated.challenge.starts_at).toLocaleString("fr-FR")}
            </p>
          )}
        </div>
      )}

      {/* Available code: split sections */}
      {validated?.status === "available" && (
        <div className="space-y-8" data-testid="choose-sections">
          {available.open.length === 0 && available.upcoming.length === 0 ? (
            <div
              data-testid="no-challenges-message"
              className="panel rounded-2xl p-8 text-center border border-cyan-400/30 bg-cyan-400/5"
            >
              <Calendar className="w-10 h-10 mx-auto mb-3 text-cyan-300" />
              <h3 className="font-display font-bold text-lg mb-2">
                Aucun défi disponible pour l&apos;instant
              </h3>
              <p className="text-sm text-white/60 max-w-md mx-auto">
                Ton code reste valide. Tu peux le retrouver dans{" "}
                <button
                  onClick={() => navigate("/mes-codes")}
                  className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2"
                  data-testid="no-challenges-mycodes-link"
                >
                  Mes codes
                </button>{" "}
                et revenir dès qu&apos;un défi est annoncé.
              </p>
            </div>
          ) : (
            <>
              {available.open.length > 0 && (
                <Section
                  title="Défis ouverts — jouer maintenant"
                  accent="cyan"
                  icon={<Flame className="w-4 h-4" />}
                  empty="Aucun défi ouvert pour le moment."
                  data-testid="open-section"
                >
                  {available.open.map((c) => (
                    <ChallengeRow
                      key={c.id}
                      challenge={c}
                      mode="play-now"
                      loading={loading}
                      onPlay={() => playNow(c.id)}
                    />
                  ))}
                </Section>
              )}
              {available.upcoming.length > 0 && (
                <Section
                  title="Défis à venir — inscription anticipée"
                  accent="fuchsia"
                  icon={<Calendar className="w-4 h-4" />}
                  empty="Aucun défi programmé acceptant les inscriptions anticipées."
                  data-testid="upcoming-section"
                >
                  {available.upcoming.map((c) => (
                    <ChallengeRow
                      key={c.id}
                      challenge={c}
                      mode="pre-register"
                      loading={loading}
                      onPlay={() => setConfirmTarget(c)}
                    />
                  ))}
                </Section>
              )}
            </>
          )}
        </div>
      )}

      {/* Pre-registration confirmation modal */}
      {confirmTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4"
          data-testid="pre-register-modal"
          onClick={() => setConfirmTarget(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="max-w-md w-full panel rounded-3xl p-7 space-y-4 border border-fuchsia-400/40"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-fuchsia-300" />
              <h3 className="font-display font-bold text-lg uppercase">
                Inscription anticipée
              </h3>
            </div>
            <p className="text-sm text-white/75 leading-relaxed">
              Attention : tu vas inscrire ton code au défi{" "}
              <b className="text-fuchsia-200">{confirmTarget.title}</b>.
            </p>
            <ul className="text-xs text-white/55 space-y-1.5 list-disc list-inside">
              <li>Ce code sera <b>définitivement réservé</b> à ce défi.</li>
              <li>Il ne pourra plus être utilisé sur un autre défi.</li>
              <li>
                Si tu ne joues pas pendant la fenêtre d&apos;ouverture, ton code
                sera perdu (no-show).
              </li>
              <li>Ton inscription compte déjà dans la cagnotte du défi.</li>
            </ul>
            <div className="flex gap-2 pt-2">
              <button
                data-testid="pre-register-cancel"
                onClick={() => setConfirmTarget(null)}
                disabled={loading}
                className="flex-1 px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 font-display uppercase tracking-wider text-sm transition-all"
              >
                Annuler
              </button>
              <button
                data-testid="pre-register-confirm"
                onClick={preRegister}
                disabled={loading}
                className="flex-1 px-4 py-3 rounded-xl bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-display uppercase tracking-wider text-sm disabled:opacity-50 transition-all"
              >
                {loading ? "..." : "Confirmer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}

function Shell({ children, wide = false }) {
  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 py-12">
      <div className="absolute inset-0 grid-bg opacity-50" />
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 via-transparent to-fuchsia-500/20" />
      <div className={`relative w-full ${wide ? "max-w-2xl" : "max-w-md"}`}>
        <div className="flex items-center justify-center gap-2 mb-8">
          <Zap className="w-6 h-6 text-cyan-400" />
          <span className="font-display font-black uppercase tracking-tighter text-xl">
            Otra<span className="text-cyan-400 neon-text-primary">Quizz</span>
          </span>
        </div>
        <div className="panel rounded-3xl p-8 scanlines relative">{children}</div>
      </div>
    </div>
  );
}

function CodeField({ code, setCode }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.25em] text-white/50 font-mono-stat block mb-1.5">
        Code personnel
      </span>
      <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-black/40 border border-white/10 focus-within:border-cyan-400/60 focus-within:glow-primary transition-all">
        <Hash className="w-4 h-4 text-cyan-300" />
        <input
          data-testid="play-code-input"
          value={code}
          onChange={(e) =>
            setCode(
              e.target.value
                .toUpperCase()
                .replace(/[^A-Z2-9]/g, "")
                .slice(0, 8)
            )
          }
          required
          className="w-full bg-transparent outline-none font-mono-stat text-xl tracking-[0.3em] text-white placeholder:text-white/30"
          placeholder="XXXXXXXX"
        />
      </div>
    </label>
  );
}

function NicknameField({ nickname, setNickname }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.25em] text-white/50 font-mono-stat block mb-1.5">
        Ton pseudo
      </span>
      <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-black/40 border border-white/10 focus-within:border-fuchsia-400/60 focus-within:glow-secondary transition-all">
        <User className="w-4 h-4 text-fuchsia-300" />
        <input
          data-testid="play-nickname-input"
          value={nickname}
          onChange={(e) => setNickname(e.target.value)}
          maxLength={24}
          required
          className="w-full bg-transparent outline-none text-white font-display text-lg"
          placeholder="Ton nom de guerrier"
        />
      </div>
    </label>
  );
}

function Section({ title, accent, icon, children, empty, ...rest }) {
  const empties =
    Array.isArray(children) ? children.length === 0 : !children;
  return (
    <section data-testid={rest["data-testid"]}>
      <div
        className={`flex items-center gap-2 mb-3 text-xs uppercase tracking-[0.3em] font-mono-stat ${
          accent === "cyan" ? "text-cyan-300" : "text-fuchsia-300"
        }`}
      >
        {icon}
        <span>{title}</span>
      </div>
      {empties ? (
        <div className="text-xs text-white/40 italic px-1 py-3">{empty}</div>
      ) : (
        <div className="space-y-3">{children}</div>
      )}
    </section>
  );
}

function ChallengeRow({ challenge, mode, onPlay, disabled, loading }) {
  const cta =
    mode === "play-now"
      ? "Jouer"
      : mode === "pre-register"
      ? "S'inscrire"
      : "Démarrer";
  const accent =
    mode === "pre-register"
      ? "bg-fuchsia-500 hover:bg-fuchsia-400 text-white"
      : "bg-cyan-400 hover:bg-cyan-300 text-black";
  const date = new Date(challenge.starts_at).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
  return (
    <div
      data-testid={`challenge-row-${challenge.id}`}
      className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-black/30 border border-white/5 hover:border-white/15 transition-all"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-display font-bold truncate">{challenge.title}</span>
          {mode === "reserved-locked" && (
            <span className="text-[10px] uppercase tracking-widest font-mono-stat px-2 py-0.5 rounded-full bg-fuchsia-400/15 text-fuchsia-200 border border-fuchsia-400/40">
              Réservé
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-white/55 font-mono-stat uppercase mt-0.5">
          <span className="flex items-center gap-1">
            <Trophy className="w-3 h-3" /> {challenge.current_prize_pool.toFixed(2)}€
          </span>
          {mode !== "play-now" && (
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" /> {date}
            </span>
          )}
        </div>
      </div>
      <button
        data-testid={`challenge-row-cta-${challenge.id}`}
        onClick={onPlay}
        disabled={loading || disabled}
        className={`px-5 py-2.5 rounded-lg font-display font-bold uppercase tracking-wider text-sm active:scale-95 disabled:opacity-50 transition-all ${accent}`}
      >
        {cta}
      </button>
    </div>
  );
}

function Terminal({ icon, title, body }) {
  return (
    <div className="text-center space-y-3" data-testid="play-terminal-state">
      <div className="flex justify-center">{icon}</div>
      <h2 className="font-display font-bold text-2xl">{title}</h2>
      <p className="text-sm text-white/60">{body}</p>
    </div>
  );
}
